import React, { useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import {
    selectCategoryData, selectClients, selectLoading,
    selectSelectedClient, selectSelectedBranch, fetchProductcategory
} from '../store/slices/productcategorySlice';
import { updateProductCategory, updateProductCategoryStatus, bulkUpdateProductCategoryStatus, saveProductCategory } from '../utilities/api';
import CategoryDialog from './CategoryDialog';
import './CategoryMaster.css';

const EFFECTIVE_STYLE = {
    today:  { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' },
    future: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
    past:   { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' },
};

const DATA_TRANSFER_STYLE = {
    pending:   { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' },
    completed: { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' },
    failed:    { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
};

const badgeStyle = (s) => ({
    ...s,
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: '0.73rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    display: 'inline-block'
});

const toLocalDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const ProductcategoryContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const categoryData  = useSelector(selectCategoryData);
    const clients       = useSelector(selectClients);
    const loading       = useSelector(selectLoading);
    const selectedClient = useSelector(selectSelectedClient);
    const selectedBranch = useSelector(selectSelectedBranch);

    const [statusFilter, setStatusFilter]         = useState('Active');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters]                   = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog]       = useState(false);
    const [editCategory, setEditCategory]         = useState(null);
    const [selectedRows, setSelectedRows]         = useState([]);
    const [bulkModal, setBulkModal]               = useState(false);
    const [bulkTarget, setBulkTarget]             = useState('');
    const [bulkEffectiveFrom, setBulkEffectiveFrom] = useState(null);

    const clientFilteredData = useMemo(() => {
        let data = categoryData;
        if (selectedClient?.client_id)
            data = data.filter((item) => String(item.client_id) === String(selectedClient.client_id));
        if (selectedBranch?.branch_id)
            data = data.filter((item) => String(item.branch_id) === String(selectedBranch.branch_id));
        return data;
    }, [categoryData, selectedClient, selectedBranch]);

    const activeCount   = useMemo(() => clientFilteredData.filter((i) => i.status === 'Active').length,   [clientFilteredData]);
    const inactiveCount = useMemo(() => clientFilteredData.filter((i) => i.status === 'Inactive').length, [clientFilteredData]);
    const filteredData  = useMemo(() => clientFilteredData.filter((i) => i.status === statusFilter),      [clientFilteredData, statusFilter]);

    const reload = () =>
        dispatch(fetchProductcategory({
            rolename:   localStorage.getItem('role')     || '',
            clientname: localStorage.getItem('clientid') || ''
        }));

    /* ── Checkbox selection with single-client constraint ────────── */
    const handleSelectionChange = (e) => {
        const newSel = e.value || [];
        if (newSel.length === 0) { setSelectedRows([]); return; }
        const firstClientId = String(newSel[0]?.client_id || '');
        const filtered = newSel.filter((r) => String(r.client_id) === firstClientId);
        if (filtered.length < newSel.length) {
            toast.current.show({ severity: 'warn', summary: 'Single Client Only', detail: 'Select records from one client at a time', life: 3000 });
        }
        setSelectedRows(filtered);
    };

    /* ── Open bulk status modal ──────────────────────────────────── */
    const openBulkModal = (newStatus) => {
        if (!selectedRows.length) return;
        setBulkTarget(newStatus);
        setBulkEffectiveFrom(null);
        setBulkModal(true);
    };

    /* ── Confirm alert → API call ────────────────────────────────── */
    const handleBulkStatusSubmit = () => {
        confirmDialog({
            message: `Change status of ${selectedRows.length} selected category(ies) to "${bulkTarget}"?`,
            header: 'Confirm Bulk Status Change',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: `Yes, set ${bulkTarget}`,
            rejectLabel: 'Cancel',
            acceptClassName: bulkTarget === 'Active' ? 'p-button-success' : 'p-button-danger',
            rejectClassName: 'p-button-text',
            accept: async () => {
                try {
                    const ids = selectedRows.map((r) => r.prcat_cat_id);
                    const payload = { ids, status: bulkTarget };
                    if (bulkEffectiveFrom) {
                        payload.effective_from = toLocalDateStr(bulkEffectiveFrom);
                    }
                    const result = await bulkUpdateProductCategoryStatus(payload);
                    if (result?.status === 'success') {
                        toast.current.show({ severity: 'success', summary: 'Updated', detail: `${selectedRows.length} category(ies) set to ${bulkTarget}`, life: 3000 });
                        setBulkModal(false);
                        setSelectedRows([]);
                        reload();
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: result?.message || 'Bulk status update failed', life: 4000 });
                    }
                } catch (err) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Bulk status update failed', life: 4000 });
                }
            }
        });
    };

    /* ── Effective From column ───────────────────────────────────── */
    const effectiveFromTemplate = (row) => {
        const val = row.effective_from;
        if (!val) return <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>;
        const d = new Date(val); d.setHours(0, 0, 0, 0);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const display = new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        const key = d.getTime() === today.getTime() ? 'today' : d > today ? 'future' : 'past';
        return <span style={badgeStyle(EFFECTIVE_STYLE[key])}>{display}</span>;
    };

    /* ── Data Transfer column ────────────────────────────────────── */
    const dataTransferTemplate = (row) => {
        const raw = row.data_transfer || '';
        if (!raw) return <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>;
        const s = DATA_TRANSFER_STYLE[raw.toLowerCase()] || { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
        return <span style={badgeStyle(s)}>{raw}</span>;
    };

    /* ── Status column ───────────────────────────────────────────── */
    const statusBodyTemplate = (row) => (
        <Tag
            value={row.status}
            severity={row.status === 'Active' ? 'success' : 'danger'}
            rounded
            style={{ minWidth: '70px', textAlign: 'center' }}
        />
    );

    /* ── Inline status toggle ────────────────────────────────────── */
    const handleStatusToggle = (row) => {
        const newStatus = row.status === 'Active' ? 'Inactive' : 'Active';
        confirmDialog({
            message: `Change status of "${row.category_name}" to ${newStatus}?`,
            header: 'Confirm Status Change',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: `Yes, set ${newStatus}`,
            rejectLabel: 'Cancel',
            acceptClassName: newStatus === 'Active' ? 'p-button-success' : 'p-button-danger',
            rejectClassName: 'p-button-text',
            accept: async () => {
                try {
                    const result = await updateProductCategoryStatus({ ids: [row.prcat_cat_id], status: newStatus });
                    if (result?.status === 'success') {
                        toast.current.show({ severity: 'success', summary: 'Updated', detail: `Status changed to ${newStatus}`, life: 3000 });
                        reload();
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: result?.message || 'Status update failed', life: 4000 });
                    }
                } catch (err) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Status update failed', life: 4000 });
                }
            }
        });
    };

    /* ── Edit save with confirmation ─────────────────────────────── */
    const handleSaveRequest = (items) => {
        const item = Array.isArray(items) ? items[0] : items;
        const payload = {
            prcat_cat_id:     item.prcat_cat_id,
            prcat_cat_name:   (item.category_name || item.prcat_cat_name || '').trim(),
            prcat_ref_name:   item.prcat_ref_name  || '',
            prcat_discount:   item.prcat_discount  ?? 0,
            prcat_tokengroup: item.prcat_tokengroup || '',
            prcat_status:     item.status          || item.prcat_status || 'Active',
            client_id:        item.client_id       || '',
            branch_id:        item.branch_id       || '',
            effective_from:   item.effective_from instanceof Date
                ? toLocalDateStr(item.effective_from)
                : item.effective_from || null,
            updated_by: localStorage.getItem('userid') || ''
        };
        confirmDialog({
            message: `Are you sure you want to update "${payload.prcat_cat_name}"?`,
            header: 'Confirm Update',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Update',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: async () => {
                try {
                    const serverResult = await updateProductCategory(payload);
                    if (serverResult?.status === 'success') {
                        try {
                            await saveProductCategory(payload);
                        } catch (clientErr) {
                            console.error('Client-side update failed:', clientErr);
                        }
                        toast.current.show({ severity: 'success', summary: 'Success', detail: serverResult.message || 'Updated successfully', life: 3000 });
                        setDisplayDialog(false);
                        setEditCategory(null);
                        reload();
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: serverResult?.message || 'Update failed', life: 4000 });
                    }
                } catch (err) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Update failed', life: 4000 });
                }
            }
        });
    };

    /* ── Actions column ──────────────────────────────────────────── */
    const actionBodyTemplate = (row) => (
        <Button
            icon="pi pi-pencil"
            rounded text
            className="p-button-warning"
            onClick={() => { setEditCategory(row); setDisplayDialog(true); }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
            style={{ width: 30, height: 30 }}
        />
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">Product Category List</h3>
                    <div className="category-status-tabs">
                        <button
                            className={`status-tab-btn active-tab${statusFilter === 'Active' ? ' selected' : ''}`}
                            onClick={() => { setStatusFilter('Active'); setSelectedRows([]); }}
                        >
                            <i className="pi pi-check-circle" />
                            Active <span className="tab-count">{activeCount}</span>
                        </button>
                        <button
                            className={`status-tab-btn inactive-tab${statusFilter === 'Inactive' ? ' selected' : ''}`}
                            onClick={() => { setStatusFilter('Inactive'); setSelectedRows([]); }}
                        >
                            <i className="pi pi-times-circle" />
                            Inactive <span className="tab-count">{inactiveCount}</span>
                        </button>
                    </div>
                </div>

                <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilterValue}
                        onChange={(e) => {
                            setGlobalFilterValue(e.target.value);
                            setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                        }}
                        placeholder="Search categories..."
                        className="w-full"
                    />
                </span>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <>
                {selectedRows.length > 0 && (
                    <div className="flex align-items-center gap-2 mb-3 p-3" style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                        <span className="font-semibold text-sm">{selectedRows.length} row{selectedRows.length > 1 ? 's' : ''} selected</span>
                        <Button label="Set Active" icon="pi pi-check-circle" className="p-button-success p-button-sm" onClick={() => openBulkModal('Active')} />
                        <Button label="Set Inactive" icon="pi pi-times-circle" className="p-button-danger p-button-sm" onClick={() => openBulkModal('Inactive')} />
                        <Button label="Clear" icon="pi pi-times" className="p-button-text p-button-secondary p-button-sm" onClick={() => setSelectedRows([])} />
                    </div>
                )}
                <div className="sub-masters-table-wrap">
                <DataTable
                    value={filteredData}
                    filters={filters}
                    globalFilterFields={['category_name', 'prcat_ref_name', 'branch_name']}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    showGridlines
                    tableStyle={{ minWidth: '62rem' }}
                    className="p-datatable-sm sub-masters-table"
                    emptyMessage={`No ${statusFilter.toLowerCase()} categories found`}
                    selection={selectedRows}
                    onSelectionChange={handleSelectionChange}
                    selectionMode="checkbox"
                    dataKey="prcat_cat_id"
                >
                    <Column selectionMode="multiple" style={{ width: '3rem' }} />
                    <Column field="prcat_cat_id"  header="ID"            sortable style={{ width: '70px' }} />
                    <Column field="category_name" header="Category Name" sortable />
                    <Column field="client_name"   header="Client"        sortable />
                    <Column field="branch_name"    header="Branch"        sortable />
                    <Column
                        header="Effective From"
                        body={effectiveFromTemplate}
                        sortable sortField="effective_from"
                        style={{ width: '145px', textAlign: 'center' }}
                    />
                    <Column
                        header="Data Transfer"
                        body={dataTransferTemplate}
                        sortable sortField="data_transfer"
                        style={{ width: '130px', textAlign: 'center' }}
                    />
                    <Column
                        field="status"
                        header="Status"
                        body={statusBodyTemplate}
                        sortable
                        style={{ width: '110px', textAlign: 'center' }}
                    />
                    <Column
                        header="Actions"
                        body={actionBodyTemplate}
                        style={{ width: '100px', textAlign: 'center' }}
                    />
                </DataTable>
                </div>
                </>
            )}

            <CategoryDialog
                visible={displayDialog}
                categoryData={editCategory}
                clients={clients}
                onHide={() => { setDisplayDialog(false); setEditCategory(null); }}
                onSave={handleSaveRequest}
            />

            {/* ── Bulk Status Modal ─────────────────────────────────── */}
            <Dialog
                visible={bulkModal}
                onHide={() => setBulkModal(false)}
                header={
                    <div className="sub-dialog-header">
                        <span className="sub-dialog-header-icon">
                            <i className={`pi ${bulkTarget === 'Active' ? 'pi-check-circle' : 'pi-times-circle'}`} />
                        </span>
                        <div className="sub-dialog-header-text">
                            Set {bulkTarget} — Effective From
                        </div>
                    </div>
                }
                modal
                draggable={false}
                style={{ width: '420px' }}
                className="sub-master-dialog"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="sub-dialog-cancel" onClick={() => setBulkModal(false)} />
                        <Button
                            label="Submit"
                            icon="pi pi-check"
                            className="sub-dialog-save"
                            onClick={handleBulkStatusSubmit}
                            disabled={!bulkEffectiveFrom}
                        />
                    </div>
                }
            >
                <div className="sub-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div style={{ padding: '0.6rem 0.9rem', background: bulkTarget === 'Active' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${bulkTarget === 'Active' ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8 }}>
                        <div className="font-semibold text-sm">{selectedRows.length} category(ies) selected</div>
                        <div className="text-xs mt-1" style={{ color: '#6b7280' }}>
                            Status will change to <strong>{bulkTarget}</strong> from the selected date.
                        </div>
                    </div>
                    <div className="sub-field-group">
                        <label className="sub-field-label">
                            <i className="pi pi-calendar sub-field-label-icon" />
                            Effective From <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <Calendar
                            value={bulkEffectiveFrom}
                            onChange={(e) => setBulkEffectiveFrom(e.value)}
                            showIcon
                            dateFormat="dd-mm-yy"
                            placeholder="Select effective date"
                            className="w-full"
                            minDate={new Date()}
                        />
                    </div>
                </div>
            </Dialog>
        </Card>
    );
};

export default ProductcategoryContent;
